import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-privacy';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy – AltaLumé ADHD Testing',
      url: `${siteUrl}/privacy`,
      description:
        'AltaLumé Mental Health Privacy Policy. Learn how we collect, use, store, and protect your personal and health information in connection with our IVA-2 ADHD attention testing services.',
      inLanguage: 'en-US',
      isPartOf: {
        '@type': 'WebSite',
        name: 'AltaLumé ADHD Testing',
        url: siteUrl,
      },
      dateModified: '2026-03-11',
    });
    if (!document.getElementById('schema-privacy')) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById('schema-privacy');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-stone-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-stone-600 mb-8">AltaLumé ADHD Testing</p>
          <p className="text-sm text-stone-500 mb-12">Effective Date: 3/11/2026</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-stone-700 leading-relaxed mb-6">
              AltaLumé Mental Health ("AltaLumé," "we," "our," or "us") is committed to protecting the privacy and security of individuals who use our website and ADHD testing services. This Privacy Policy describes how we collect, use, store, and disclose information when you access our website or utilize our computerized attention testing services.
            </p>

            <p className="text-stone-700 leading-relaxed mb-8">
              AltaLumé provides computerized attention and response-control testing using the IVA-2 Continuous Performance Test. Our service provides objective testing results intended for informational purposes only and does not provide medical diagnoses or treatment.
            </p>

            <p className="text-stone-700 leading-relaxed mb-12">
              By using our website or services, you agree to the practices described in this Privacy Policy.
            </p>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">1. Information We Collect</h2>
              <p className="text-stone-700 leading-relaxed mb-6">
                We may collect the following categories of information when you use our website or services.
              </p>

              <h3 className="text-xl font-semibold text-stone-900 mb-3">Personal Information</h3>
              <p className="text-stone-700 leading-relaxed mb-3">
                Information you voluntarily provide to us, including:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>Name</li>
                <li>Date of birth</li>
                <li>Email address</li>
                <li>Phone number (if provided)</li>
                <li>State or location at the time of testing</li>
                <li>Payment information</li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-900 mb-3">Testing Data</h3>
              <p className="text-stone-700 leading-relaxed mb-3">
                Information generated during the ADHD attention testing process, including:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>Test date and time</li>
                <li>IVA-2 performance metrics</li>
                <li>Attention and response-control scores</li>
                <li>Validity indicators</li>
                <li>Generated testing reports</li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-900 mb-3">Technical Information</h3>
              <p className="text-stone-700 leading-relaxed mb-3">
                When you use our website, we may automatically collect certain technical information, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-stone-700 space-y-2">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Pages visited</li>
                <li>Cookies and analytics data</li>
              </ul>
              <p className="text-stone-700 leading-relaxed">
                This information helps us improve website functionality and user experience.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-stone-700 leading-relaxed mb-3">
                AltaLumé may use your information for the following purposes:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>To provide computerized attention testing services</li>
                <li>To generate and deliver testing reports</li>
                <li>To communicate with you regarding your test results or service inquiries</li>
                <li>To verify identity and maintain testing records</li>
                <li>To process payments</li>
                <li>To maintain system security and prevent fraud</li>
                <li>To comply with legal and regulatory obligations</li>
              </ul>
              <p className="text-stone-700 leading-relaxed">
                Testing results are provided solely for informational purposes and are intended to be reviewed by your personal healthcare provider as part of a comprehensive evaluation.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">3. Health Information and HIPAA</h2>
              <p className="text-stone-700 leading-relaxed mb-4">
                Certain information collected through our testing services may be considered health-related information.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                AltaLumé maintains administrative, technical, and physical safeguards designed to protect the confidentiality and security of this information in accordance with applicable privacy laws and industry standards.
              </p>
              <p className="text-stone-700 leading-relaxed">
                However, AltaLumé's ADHD testing service does not constitute medical diagnosis, treatment, or clinical care.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">4. How We Share Information</h2>
              <p className="text-stone-700 leading-relaxed mb-6">
                AltaLumé does not sell personal information.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                We may share information in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-stone-900 mb-3">Service Providers</h3>
              <p className="text-stone-700 leading-relaxed mb-3">
                We may share information with trusted third-party service providers who assist with:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>Website hosting</li>
                <li>Payment processing</li>
                <li>Data storage</li>
                <li>Testing software platforms</li>
                <li>Analytics services</li>
              </ul>
              <p className="text-stone-700 leading-relaxed mb-6">
                These vendors are required to protect your information and use it only for authorized purposes.
              </p>

              <h3 className="text-xl font-semibold text-stone-900 mb-3">Legal Compliance</h3>
              <p className="text-stone-700 leading-relaxed mb-3">
                We may disclose information if required to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-stone-700 space-y-2">
                <li>Comply with applicable laws or regulations</li>
                <li>Respond to lawful requests by public authorities</li>
                <li>Enforce our Terms of Service</li>
                <li>Protect the rights or safety of users or others</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">5. Data Retention</h2>
              <p className="text-stone-700 leading-relaxed mb-4">
                AltaLumé retains testing records and associated information for a period consistent with applicable laws and operational requirements.
              </p>
              <p className="text-stone-700 leading-relaxed mb-3">
                Testing records may be retained for several years in order to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>Provide copies of reports upon request</li>
                <li>Maintain testing integrity</li>
                <li>Comply with regulatory obligations</li>
              </ul>
              <p className="text-stone-700 leading-relaxed">
                After this period, records may be securely deleted or anonymized.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">6. Data Security</h2>
              <p className="text-stone-700 leading-relaxed mb-4">
                We implement reasonable administrative, technical, and physical safeguards to protect personal information from unauthorized access, loss, misuse, or alteration.
              </p>
              <p className="text-stone-700 leading-relaxed">
                Despite these protections, no internet-based system can be guaranteed to be completely secure. Users should take precautions when transmitting personal information online.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">7. Cookies and Analytics</h2>
              <p className="text-stone-700 leading-relaxed mb-3">
                Our website may use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-stone-700 space-y-2">
                <li>Improve website performance</li>
                <li>Analyze user activity</li>
                <li>Enhance user experience</li>
              </ul>
              <p className="text-stone-700 leading-relaxed">
                Users may adjust browser settings to disable cookies, though some website features may not function properly without them.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4">8. Your Privacy Rights</h2>
              <p className="text-stone-700 leading-relaxed mb-3">
                Depending on your location, you may have rights regarding your personal information, including the right to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-stone-700 space-y-2">
                <li>Request access to your information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of certain data where permitted by law</li>
              </ul>
            </section>
          </div>

          {/* Footer links */}
          <div className="flex flex-wrap gap-4 pt-6 border-t border-stone-100">
            <Link to="/" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">← Back to Home</Link>
            <Link to="/hipaa-notice" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">HIPAA Notice</Link>
            <Link to="/terms" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">Terms of Service</Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}